// The envelope-routing decision (chat-mls.md CH3), pure so the tests can
// walk every branch without wasm or a server. Getting one of these wrong
// forks the local group state — this is the file to be paranoid about.

export interface EnvelopeFacts {
  kind: 'commit' | 'welcome' | 'application' | 'proposal';
  seq: number;
  senderDevice: string;
}

export interface ConvState {
  myDevice: string;
  /** Has this device joined the MLS group yet? */
  joined: boolean;
  /** The seq of the welcome we joined through (0 when creator). */
  joinSeq: number;
}

export type EnvelopeAction = 'skip' | 'join' | 'process';

/**
 * What to do with one replayed envelope:
 *
 * - Our own sends are skipped — MLS senders cannot decrypt their own
 *   application messages, and we already echoed locally.
 * - Before joining, the ONLY thing we can act on is a welcome (the server
 *   already filtered welcomes to our device). Everything earlier belongs to
 *   epochs we were never in — undecryptable by design, not an error.
 * - After joining, anything at or before the welcome's seq is history from
 *   before we existed (including the very commit that added us — the welcome
 *   REPLACES it); later envelopes are processed in seq order.
 */
export function envelopeAction(e: EnvelopeFacts, s: ConvState): EnvelopeAction {
  if (e.senderDevice === s.myDevice) return 'skip';
  if (!s.joined) return e.kind === 'welcome' ? 'join' : 'skip';
  if (e.seq <= s.joinSeq) return 'skip';
  if (e.kind === 'welcome') return 'skip'; // somebody else's door, not ours
  return 'process';
}

/**
 * Devices missing from an MLS group (CH6 multi-device). Given the server's
 * expected roster (every active device of the member users) and the group's
 * current device ids, returns the devices to add — never our own, never one
 * already present. Pure so the convergence logic is testable without wasm.
 */
export function missingDevices(
  expected: { userId: string; deviceId: string }[],
  present: string[],
  myDevice: string,
): { userId: string; deviceId: string }[] {
  const have = new Set(present);
  return expected.filter((d) => d.deviceId !== myDevice && !have.has(d.deviceId));
}
